import {
    NavigateFunction,
    NavigateOptions,
    To,
    useNavigate as useRouterNavigate,
} from "react-router-dom"
import { ClientSideLocalize } from "../localization/client-side/Dictionary"

/** Returns a function that can be used to navigate to a new route.
 *
 * This should be used instead of `react-router-dom`'s equivalent function, as this one supports
 * `useConfirmNavigation`.
 *
 *  Support for intercepting navigation was removed in `react-router-dom` v6, not to be added back
 *  it seems. https://github.com/remix-run/react-router/issues/8139
 *
 * */
export function useNavigate(): NavigateFunction {
    const routerNavigate = useRouterNavigate()

    return function (to: To | number, options?: NavigateOptions) {
        if (useNavigate.unsavedChanges.size > 0) {
            if (
                !confirm(
                    ClientSideLocalize({
                        en: "You have unsaved changes, that will stay as draft but not be applied if you leave. Are you sure you want to leave?",
                        no: "Du har endringer som ikke er lagret, men som forblir som utkast hvis du forlater siden. Er du sikker på at du vil forlate siden?",
                        sv: "Du har ändringar som inte är sparade, men som förblir som utkast om du lämnar sidan. Är du säker på att du vill lämna sidan?",
                        da: "Du har ændringer, der ikke er gemt, men som forbliver som kladde, hvis du forlader siden. Er du sikker på, at du vil forlade siden?",
                    })
                )
            ) {
                return
            }
        }

        // Call the right overload for "number"
        if (typeof to === "number") routerNavigate(to)
        else routerNavigate(to, options)
    }
}
/**
 * A set of things that contain unsaved changes, that should be checked before
 * navigating away from the page. If this set is non-empty, the user will be
 * prompted to confirm navigation.
 */
useNavigate.unsavedChanges = new Set<any>()
